import { FC, PropsWithChildren, useState } from 'react';
import { useDebouncedState } from 'utils/hooks/useDebouncedState';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { PAGE_SIZES } from 'ui/paginationFooter';
import { CriteriaIndexQueryContext } from './CriteriaIndexQueryContext';
import { CriteriaTableColumn, CriteriaTableTab } from '../types';
import { useQueryAnonCriterias } from '../hooks/useQueryAnonCriterias';
import { useQueryActiveCriteriaCount } from '../hooks/useQueryActiveCriteriaCount';

export const CriteriaIndexQueryProvider: FC<PropsWithChildren> = ({ children }) => {
  const [selectedTab, setSelectedTab] = useState<CriteriaTableTab>(CriteriaTableTab.ALL);
  const [debouncedSearch, search, setSearch] = useDebouncedState('', 300);
  const [sortedBy, setSortedBy] = useState<CriteriaTableColumn | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<SORT_DIRECTIONS>(SORT_DIRECTIONS.DESCENDING);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

  const {
    results,
    totalCount,
    isLoading,
    refetch: refetchCriterias,
  } = useQueryAnonCriterias(
    selectedTab,
    debouncedSearch,
    sortedBy,
    sortDirection,
    pageNum,
    pageSize,
  );

  const { limitReached, refetch: refetchActiveCount } = useQueryActiveCriteriaCount();

  const handleRefetch = () => {
    refetchCriterias();
    refetchActiveCount();
  };

  return (
    <CriteriaIndexQueryContext.Provider
      value={{
        stateFilterTab: selectedTab,
        setStateFilterTab: setSelectedTab,
        searchTextInput: search,
        searchTextQuery: debouncedSearch,
        setSearchText: setSearch,
        sortedBy,
        pageNum,
        pageSize,
        setSortedBy,
        sortDirection,
        setSortDirection,
        criterias: results,
        criteriasLoading: isLoading,
        totalCriteriaCount: totalCount,
        refetchCriterias: handleRefetch,
        maxActiveCriteriaLimitReached: limitReached,
        setPageNum,
        setPageSize,
      }}
    >
      {children}
    </CriteriaIndexQueryContext.Provider>
  );
};
